'use client';

import { Cart } from '@peerless/data-access/cart-service';
import { usePathname, useRouter } from '@/navigation';
import { useState, PropsWithChildren, useMemo, useCallback } from 'react';
import { NavigationListener } from '@peerless/ui-navigation';
import { useAuthenticated } from '@peerless/ui-amplify';
import { loadCartServerAction, addToCartServerAction, updateCartServerAction } from './actions';
import { CartSidebarUIStateContext } from './CartSidebarUIStateContext';
import { useCurrentLocale } from '@peerless/ui-locale';

type RouterType = ReturnType<typeof useRouter>;
export function CartSidebarUIStateProvider({
  loadCartServerAction: prop_loadCartServerAction,
  children,
}: PropsWithChildren<{
  loadCartServerAction?(): Promise<{
    isAuthenticated: boolean;
    cartData: Cart | undefined;
  }>;
}>) {
  'use client';
  const router = useRouter();
  const authStatus = useAuthenticated();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingCartData, setIsLoadingCartData] = useState(false);
  const [cartData, setCartData] = useState<Cart | undefined>(undefined);
  const { locale } = useCurrentLocale();
  const pathname = usePathname();

  const refetchData = useCallback(async () => {
    setIsLoadingCartData(true);
    try {
      const res = await loadCartServerAction(locale);
      if (!res || needsRedirect(router, res) || !res?.cartData) {
        setIsLoadingCartData(false);
        return;
      }
      setCartData(res.cartData);
      setIsLoadingCartData(false);
      return res as { cartData?: Cart; redirect?: string };
    } catch (err) {
      console.error('Error loading cart data', err);
    } finally {
      setIsLoadingCartData(false);
    }
  }, [router, locale]);

  const _setIsOpen = useCallback(
    async (open: boolean) => {
      // Triggered from the cart button.  This could cause a redirect to login,
      // or a redirect to the cart page, or open the sidebar depending on the current route

      if (!authStatus) {
        // if not logged in redirect to sign in
        console.log('redirecting to sign in');
        const encooded = encodeURIComponent(`/cart`);
        router.push(`/sign-in?returnUrl=${encooded}`);
      } else if (isCheckoutPath(pathname)) {
        // if in a later checkout step, go back to cart page
        router.push(`/cart`);
      } else if (!isCartPath(pathname)) {
        // if we are on cart page, don't do anything,
        // otherwise toggle the sidebar and refetch the data if opening
        if (open) {
          const res = await refetchData();
          // if the refetch failed and returned a redirect, follow it
          if (!res || needsRedirect(router, res) || !res?.cartData) {
            setIsLoadingCartData(false);
            return;
          }
        }
        setIsOpen(open);
      }
    },
    [authStatus, pathname, router, refetchData]
  );
  const toggleIsOpen = useCallback(() => {
    _setIsOpen(!isOpen);
  }, [isOpen, _setIsOpen]);

  const addProductToCart = useCallback(
    async (skus: string | string[]) => {
      try {
        setIsLoadingCartData(true);
        const res = await addToCartServerAction(skus, pathname, locale);
        if (!res || needsRedirect(router, res) || !res?.cartData) {
          setIsLoadingCartData(false);
          return;
        }

        setCartData(res.cartData);
        _setIsOpen(true);
      } catch (err) {
        console.error('Error adding cart data', err);
      } finally {
        setIsLoadingCartData(false);
      }
    },
    [pathname, _setIsOpen, router, locale]
  );
  const updateProductInCart = useCallback(
    async (sku: string, quantity: number) => {
      try {
        setIsLoadingCartData(true);
        const res = await updateCartServerAction(sku, quantity, locale);
        if (!res || needsRedirect(router, res) || !res?.cartData) {
          setIsLoadingCartData(false);
          return;
        }
        setCartData(res.cartData);
        _setIsOpen(true);
      } finally {
        setIsLoadingCartData(false);
      }
    },
    [_setIsOpen, router, locale]
  );

  const providerValue = useMemo(() => {
    return {
      isOpen: isOpen,
      toggleIsOpen,
      setIsOpen: _setIsOpen,
      isLoadingCartData,
      setIsLoadingCartData,
      cartData,
      setCartData,
      isAuthenticated: authStatus,
      refetchData,
      isCheckoutPath: isCheckoutPath(pathname),

      addProductToCart,
      updateProductInCart,
    };
  }, [
    isOpen,
    _setIsOpen,
    isLoadingCartData,
    setIsLoadingCartData,
    refetchData,
    setCartData,
    cartData,
    authStatus,
    pathname,
    addProductToCart,
    updateProductInCart,
    toggleIsOpen,
  ]);

  const closeSidebar = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <CartSidebarUIStateContext.Provider value={providerValue}>
      <NavigationListener onNavigate={closeSidebar} />
      {children}
    </CartSidebarUIStateContext.Provider>
  );
}

const checkoutPaths = ['/checkout', '/shipping'];

const isCheckoutPath = (pathname: string) => {
  return checkoutPaths.some(path => pathname.indexOf(path) > -1);
};
const isCartPath = (pathname: string) => {
  return pathname.indexOf('/cart') > -1;
};

function needsRedirect(
  router: RouterType,
  res: { redirect?: string; cartData?: Cart }
): res is { redirect: string } {
  const r = res as { redirect: string };
  if (r.redirect) {
    // push back onto the macro task queue
    setTimeout(() => {
      r.redirect && router.push(r.redirect);
    }, 100);
    return true;
  }
  const c = res as { cartData: Cart };
  if (!c?.cartData) {
    return true;
  }
  return false;
}
